import { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { Conversation, Message } from 'src/api/v1-api/helpers';
import { useAuth } from 'src/utils/auth';
import useRequest from 'src/utils/hooks/useRequest';
import { useMarketplaceContext } from './marketplace';
import * as Sentry from '@sentry/nextjs';
import { useSSRSafeFeatureFlag } from 'src/utils/hooks';

type MessagingContextProps = {
  conversations: Conversation[] | null;
  conversationsMessages: { [key: number]: Message[] };
  notificationsCount: number;
  refreshConversations: () => void;
  fetchConversationMessages: (conversationId: number) => void;
  addMessageToConversation: (conversationId: number, body: string) => void;
  updateConversationReadStatus: (conversationId: number) => void;
  messagingLoading: boolean;
  startConversation: (
    marketplaceId: number,
    contextType: string,
    subjectObjectId: number,
    otherAccountId: number,
    contextTitle: string,
  ) => Promise<Conversation | null>;
};

export const MessagingContext = createContext<MessagingContextProps>({
  conversations: null,
  conversationsMessages: {},
  notificationsCount: 0,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  refreshConversations: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  fetchConversationMessages: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  addMessageToConversation: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  updateConversationReadStatus: () => {},
  messagingLoading: false,
  startConversation() {
    return Promise.resolve(null);
  },
});

interface MessagingProviderProps {
  children: React.ReactNode;
}

export const MessagingProvider = ({ children }: MessagingProviderProps) => {
  const { api } = useRequest();
  const [conversations, setConversations] = useState<Conversation[] | null>(null);
  const [conversationsMessages, setConversationsMessages] = useState<{ [key: number]: Message[] }>(
    {},
  );
  const [notificationsCount, setNotificationsCount] = useState<number>(0);
  const { marketplace } = useMarketplaceContext();
  const { user, isAuthenticated } = useAuth();
  const [messagingLoading, setMessagingLoading] = useState(false);
  const currentAccountId =
    (user &&
      marketplace &&
      user.marketplace_accounts.find((account) => account.marketplace_id === marketplace.id)?.id) ||
    0;
  const messagingEnabled = useSSRSafeFeatureFlag('messaging');

  const refreshConversations = useCallback(async () => {
    if (marketplace?.id && isAuthenticated) {
      try {
        const data = await api.marketplace.messaging.conversation.list(marketplace.id);
        // only update if there are new conversations or if the last_message_id is different
        setConversations((prevConversations) => {
          if (
            prevConversations === null ||
            data.length > prevConversations.length ||
            data.some(
              (conversation) =>
                conversation.last_message_id !==
                prevConversations?.find((c) => c.id === conversation.id)?.last_message_id,
            )
          ) {
            return data;
          }
          return prevConversations;
        });
      } catch (error) {
        Sentry.captureException(error);
        setConversations([]);
      }
    }
  }, [api.marketplace.messaging.conversation, marketplace?.id, isAuthenticated]);

  useEffect(() => {
    if (messagingEnabled && marketplace?.id && isAuthenticated) {
      refreshConversations();
    }
  }, [messagingEnabled, marketplace?.id, isAuthenticated]);

  // TODO: This is a barebones approach that should be improved by using server sent events or a websocket.
  // Auto-refresh conversations every minute while user is on the site
  useEffect(() => {
    if (
      !messagingEnabled ||
      !marketplace?.id ||
      !isAuthenticated ||
      typeof window === 'undefined'
    ) {
      return;
    }

    let isPageVisible = true;
    const refreshInterval = () => {
      if (isPageVisible) {
        refreshConversations();
      }
    };

    const intervalId = setInterval(refreshInterval, 60000); // 60 seconds

    // Handle page visibility changes
    const handleVisibilityChange = () => {
      isPageVisible = !document.hidden;
      if (isPageVisible) {
        // Refresh immediately when page becomes visible
        refreshConversations();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [marketplace?.id, isAuthenticated, refreshConversations]);

  const updateNotificationsCount = () => {
    if (!user?.id || conversations === null || conversations.length === 0) {
      return;
    }

    let count = 0;
    conversations.forEach((conversation) => {
      const userParticipant = conversation.participants.find(
        (participant) => participant.marketplace_account_id === currentAccountId,
      );
      if (userParticipant) {
        if (userParticipant.most_recent_message_id !== conversation.last_message_id) {
          count += 1;
        }
      }
    });
    setNotificationsCount(count);
  };

  useEffect(() => {
    if (marketplace?.id && user?.id && conversations !== null && conversations.length > 0) {
      updateNotificationsCount();
    }
  }, [conversations, marketplace?.id, user?.id]);

  // TODO: add pagination
  const fetchConversationMessages = useCallback(
    async (conversationId: number) => {
      if (isAuthenticated) {
        try {
          setMessagingLoading(true);
          const data = await api.marketplace.messaging.message.get(conversationId);
          // update if there are any messages not already in the conversationsMessages
          setConversationsMessages((prevMessages) => {
            const hasNewMessages =
              prevMessages[conversationId] === undefined ||
              data.results.some(
                (message) => !prevMessages[conversationId]?.some((m) => m.id === message.id),
              );

            if (hasNewMessages) {
              return {
                ...prevMessages,
                [conversationId]: data.results,
              };
            }
            return prevMessages;
          });
        } catch (error) {
          Sentry.captureException(error);
        } finally {
          setMessagingLoading(false);
        }
      }
    },
    [api.marketplace.messaging.message, isAuthenticated],
  );

  const addMessageToConversation = useCallback(
    async (conversationId: number, body: string) => {
      if (isAuthenticated) {
        await api.marketplace.messaging.message.create(conversationId, { body });

        // update the conversation with the new message
        fetchConversationMessages(conversationId);
      }
    },
    [fetchConversationMessages, api.marketplace.messaging.message, isAuthenticated],
  );

  const updateConversationReadStatus = useCallback(
    (conversationId: number) => {
      setConversations((prevConversations) => {
        const conversationLastMessageId = prevConversations?.find(
          (c) => c.id === conversationId,
        )?.last_message_id;

        if (!conversationLastMessageId) return prevConversations;

        // update the current user's most recent message id immediately for UI update
        const updatedConversations = prevConversations?.map((conversation) =>
          conversation.id === conversationId
            ? {
                ...conversation,
                participants: conversation.participants.map((participant) =>
                  participant.marketplace_account_id === currentAccountId
                    ? { ...participant, most_recent_message_id: conversation.last_message_id }
                    : participant,
                ),
              }
            : conversation,
        );

        // update the read status in the backend
        if (isAuthenticated) {
          api.marketplace.messaging.message.read(conversationId, conversationLastMessageId);
        }

        return updatedConversations;
      });
    },
    [currentAccountId, api.marketplace.messaging.message, isAuthenticated],
  );

  const startConversation = useCallback(
    async (
      marketplaceId: number,
      contextType: string,
      subjectObjectId: number,
      otherAccountId: number,
      contextTitle: string,
    ) => {
      if (isAuthenticated) {
        try {
          const data = await api.marketplace.messaging.conversation.create(marketplaceId, {
            kind: contextType,
            title: `Re: ${contextTitle}`,
            subject_object_id: subjectObjectId,
            participant_account_ids: [otherAccountId],
          });
          refreshConversations();
          return data;
        } catch (error) {
          Sentry.captureException(error);
          return null;
        }
      } else {
        return null;
      }
    },
    [api.marketplace.messaging.conversation, refreshConversations, isAuthenticated],
  );

  const contextValue = useMemo(
    () => ({
      conversations,
      conversationsMessages,
      notificationsCount,
      refreshConversations,
      fetchConversationMessages,
      addMessageToConversation,
      updateConversationReadStatus,
      messagingLoading,
      startConversation,
    }),
    [
      conversations,
      conversationsMessages,
      notificationsCount,
      refreshConversations,
      fetchConversationMessages,
      addMessageToConversation,
      updateConversationReadStatus,
      messagingLoading,
      startConversation,
    ],
  );
  return <MessagingContext.Provider value={contextValue}>{children}</MessagingContext.Provider>;
};

export const useMessaging = () => useContext(MessagingContext);
